import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, TextField, Box, Divider, IconButton, Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Select, notification } from 'antd';
import { useForm, Controller } from 'react-hook-form';
import { apiCategortList, generateApiKeyAndToken } from '../../services';
import { getKeyPairById, editKeyPair } from '../../services/keysPairService';
import { saveApiKeyAndToken } from '../../services/saveKeysService';
import { ErrorText } from '../../components';

const { Option } = Select;

const EditDialogBox = ({ open, handleClose, handleSave, editData }) => {
  const [apiList, setApiList] = useState([]);
  const [customError, setCustomError] = useState('');
  const editId = editData?.id;

  const { control, handleSubmit, setValue, formState: { errors }, register, trigger } = useForm({
    mode: 'onChange',
    defaultValues: {
      name: '',
      apiKey: '',
      apiSecret: '',
      selectedApis: [],
    }
  });

  useEffect(() => {
    fetchCreditsPerApi();
    if (editId) {
      fetchApiDataById(editId);
    } else {
      handleRegenerate();
    }
  }, [editId]);

  const handleRegenerate = async () => {
    try {
      const res = await generateApiKeyAndToken();
      if (res?.data) {
        const { apiKey, apiSecret } = res.data.data;
        setValue('apiKey', apiKey);
        setValue('apiSecret', apiSecret);
      } else {
        console.error('Response data is empty');
      }
    } catch (error) {
      console.error('Error regenerating API key and secret:', error);
    }
  };

  const fetchCreditsPerApi = async () => {
    try {
      const response = await apiCategortList();
      const apisByCategory = response.data?.data?.attributes?.apisByCategory;
      if (apisByCategory) {
        const apiList = [];

        Object.values(apisByCategory).forEach(category => {
          category.apis.forEach(api => {
            apiList.push({
              value: api.id,
              label: api.name,
            });
          });
        });

        setApiList(apiList);
      }
    } catch (error) {
      console.error('Error fetching credits per API:', error);
    }
  };

  const fetchApiDataById = async (id) => {
    try {
      const response = await getKeyPairById(id);
      const { apiKey, apiSecret, apiAccessKeysName, apis } = response.data.data.attributes;
      const selectedApis = apis?.data?.map((api) => api.id) || [];
      setValue('name', apiAccessKeysName || '');
      setValue('apiKey', apiKey || '');
      setValue('apiSecret', apiSecret || '');
      setValue('selectedApis', selectedApis);
    } catch (error) {
      console.error('Error fetching API key details:', error);
    }
  };

  const onSubmit = async (data) => {
    setCustomError('');
    const isFormValid = await trigger();
    if (!isFormValid) return;

    try {
      if (editId) {
        const requestData = {
          apiAccessKeysName: data.name,
          apis: data.selectedApis,
        };
        await editKeyPair(editId, { data: requestData });
        notification.success({
          message: "Updated",
          description: "API key updated successfully.",
        });
      } else {
        const requestData = {
          apiKey: data.apiKey,
          apiSecret: data.apiSecret,
          apiAccessKeysName: data.name,
          apis: data.selectedApis,
        };
        const saveRes = await saveApiKeyAndToken(requestData);
        if (saveRes.status === 200) {
          notification.success({
            message: "Created",
            description: "API key created successfully.",
          });
        } else {
          notification.error({
            message: "Error",
            description: "Failed to save API key.",
          });
        }
      }
      handleSave();
      handleClose();
    } catch (error) {
      if (error.response?.data?.error?.message) {
        setCustomError('This name already exists. Please try another one.');
        notification.error({
          message: "Error",
          description: "This name already exists. Try another one.",
        });
      } else {
        setCustomError('An unexpected error occurred. Please try again.');
        notification.error({
          message: "Error",
          description: "Unexpected error occurred.",
        });
      }
    }
  };




  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h4">
          {editData ? "Edit API Key" : "Create API Key"}
        </Typography>
        <Tooltip title="Close">
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <Divider sx={{ marginTop: 0 }} />
      <DialogContent sx={{ p: 0, overflowY: 'auto' }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row", md: "row" },
            gap: 2,
            paddingTop: 4,
          }}
        >
          <Box flex={1} pl={{ xs: 2, sm: 5, md: 5 }} pr={{ xs: 2, sm: 5, md: 4 }}>
            <Typography variant="h5">Name</Typography>
            <Controller
              name="name"
              control={control}
              rules={{
                required: "Name is required",
                minLength: {
                  value: 3,
                  message: "Name must be at least 3 characters long.",
                },
                maxLength: {
                  value: 50,
                  message: "Name must be less than 50 characters.",
                },
              }}
              render={({ field }) => (
                <TextField
                  fullWidth
                  placeholder="Example: Development Environment API"
                  variant="outlined"
                  margin="normal"
                  {...field}
                  error={!!errors.name}
                  onChange={(e) => {
                    setCustomError("");
                    field.onChange(e);
                  }}
                />
              )}
            />
            <ErrorText>{errors.name?.message}</ErrorText>
            {customError && <ErrorText>{customError}</ErrorText>}
            <Typography variant="h5" mt={3}>
              Select API
            </Typography>
            <Controller
              name="selectedApis"
              control={control}
              rules={{ required: "Select at least one API" }}
              render={({ field }) => (
                <Select
                  size="large"
                  mode="multiple"
                  dropdownStyle={{ zIndex: 1500 }}
                  showSearch={false}
                  style={{
                    width: "100%",
                    marginTop: "16px",
                  }}
                  placeholder="Please Select"
                  onChange={(value) => {
                    field.onChange(value);
                    setValue("selectedApis", value);
                  }}
                  {...field}
                  value={field.value}
                  error={!!errors.selectedApis}
                >
                  {apiList.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              )}
            />
            <ErrorText>{errors.selectedApis?.message}</ErrorText>
          </Box>
          <Box
            flex={1}
            pl={{ xs: 2, sm: 3, md: 3 }}
            pr={{ xs: 2, sm: 3, md: 8 }}
            pb={{ xs: 4, sm: 3, md: 9 }}
            sx={{
              backgroundColor: "rgba(245, 245, 245, 1)",
              mt: { xs: 2, sm: 0, md: 0 },
            }}
          >
            {" "}
            <Typography variant="h5">API Key</Typography>
            <TextField
              sx={{ backgroundColor: "#002453", color: "#ffffff" }}
              fullWidth
              {...register("apiKey")}
              variant="outlined"
              margin="normal"
              InputProps={{
                readOnly: true,
                style: { color: "#ffffff" },
              }}
            />
            <Typography variant="h5" mt={3}>
              API Secret Key
            </Typography>
            <TextField
              sx={{ backgroundColor: "#002453" }}
              fullWidth
              {...register("apiSecret")}
              variant="outlined"
              margin="normal"
              InputProps={{
                readOnly: true,
                style: { color: "#ffffff" },
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      <Divider sx={{ marginTop: 0 }} />
      <DialogActions>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            border: "solid 1px #03A9F4",
            textTransform: "none",
            fontSize: "14px",
            borderRadius: 2,
            px: 1.5,
            py: 1,
            color: "#03A9F4",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          sx={{
            backgroundColor: "#03A9F4",
            textTransform: "none",
            fontSize: "14px",
            borderRadius: 2,
            px: 1.5,
            py: 1,
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EditDialogBox.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  handleSave: PropTypes.func,
  editData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

export default EditDialogBox;
